import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2, Save, Briefcase, Users, ChevronDown } from "lucide-react";
import { getCompany, updateCompany } from "@/lib/api";
import { toast } from "sonner";
import DateField from "@/components/DateField";

const EMPTY_DIRECTOR = { name: "", din: "", designation: "Director", appointment_date: "", nationality: "Indian" };
const EMPTY_MEMBER = {
  name: "", email: "", member_type: "Equity", shares_held: 0, shareholding_percent: 0,
  address: "", din: "", source: "",
  // KYC + register-grade fields (iter-37)
  folio_no: "", face_value: 0, father_name: "", pan_no: "", unique_id_no: "",
  occupation: "", nationality: "Indian", status: "Individual",
};
const EMPTY_PROFESSIONAL = {
  name: "", role: "Statutory Auditor", firm_name: "", membership_no: "",
  firm_registration_no: "", email: "", phone: "",
  appointment_date: "", term_end_date: "", notes: "",
};

const ROLE_OPTIONS = [
  "Statutory Auditor", "Internal Auditor", "Cost Auditor", "Secretarial Auditor",
  "Company Secretary", "Tax Advisor", "Legal Advisor", "Other",
];
const COMPANY_TYPE_OPTIONS = ["Private Limited", "Public Limited", "LLP", "OPC"];
const MEMBER_TYPE_OPTIONS = ["Equity", "Preference"];
const MEMBER_STATUS_OPTIONS = ["Individual", "Body Corporate", "Trust", "HUF", "LLP", "Partnership", "Government", "Other"];

const _rowId = () => `r${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

export default function EditCompany() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    getCompany(id)
      .then((r) => {
        const d = r.data;
        setForm({
          name: d.name || "",
          cin: d.cin || "",
          company_type: d.company_type || "Private Limited",
          registered_address: d.registered_address || "",
          email: d.email || "",
          incorporation_date: d.incorporation_date || "",
          authorized_capital: d.authorized_capital || "",
          paid_up_capital: d.paid_up_capital || "",
          fy_end_date: d.fy_end_date || "",
          directors: (d.directors?.length ? d.directors : [{ ...EMPTY_DIRECTOR }]).map((x) => ({ ...EMPTY_DIRECTOR, ...x, _rid: x._rid || _rowId() })),
          members: (d.members || []).map((m) => ({ ...EMPTY_MEMBER, ...m, _rid: m._rid || _rowId() })),
          professionals: (d.professionals || []).map((p) => ({ ...EMPTY_PROFESSIONAL, ...p, _rid: p._rid || _rowId() })),
        });
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        const status = e.response?.status;
        if (status === 403) {
          setError(detail || "You don't have access to this company.");
        } else if (status === 404) {
          setError("Company not found. It may have been deleted or you don't have access to it on this workspace.");
        } else {
          setError(detail || "Could not load this company. Please check your connection and try again.");
        }
        toast.error(detail || "Could not load company");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateArrayItem = (key, i, k, v) =>
    setForm((f) => ({ ...f, [key]: f[key].map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const addArrayItem = (key, empty) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { ...empty, _rid: _rowId() }] }));
  const removeArrayItem = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      // Drop fully-empty directors / members / professionals
      const cleanDirectors = form.directors.filter((d) => d.name.trim());
      const cleanMembers = (form.members || []).filter((m) => m.name.trim());
      const cleanProfessionals = (form.professionals || []).filter((p) => p.name.trim());
      await updateCompany(id, {
        ...form,
        directors: cleanDirectors,
        members: cleanMembers,
        professionals: cleanProfessionals,
      });
      toast.success("Company updated");
      navigate(`/companies/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto p-10 text-zinc-500"><Loader2 size={14} className="inline animate-spin" /> Loading company…</div>;
  }
  if (error || !form) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="edit-company-error">
        <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="border border-rose-200 bg-rose-50 p-6">
          <div className="label-eyebrow text-rose-700 mb-2">Cannot edit this company</div>
          <p className="text-sm text-rose-800">{error || "Company not found."}</p>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={() => navigate("/companies")} className="swiss-btn-ghost" data-testid="back-to-companies">
            Back to Companies
          </button>
          <button onClick={() => window.location.reload()} className="swiss-btn-primary" data-testid="retry-edit">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10" data-testid="edit-company-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="label-eyebrow mb-2">Master Data · Edit</div>
      <h1 className="font-heading text-4xl font-light tracking-tight mb-8">{form.name || "Edit Company"}</h1>

      <div className="space-y-10" data-testid="edit-form">
        {/* Company section */}
        <section className="border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 px-6 py-4">
            <div className="label-eyebrow">Company</div>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name *" value={form.name} onChange={(v) => setField("name", v)} testId="field-name" />
            <Field label="CIN" value={form.cin} onChange={(v) => setField("cin", v)} testId="field-cin" />
            <SelectField label="Type" value={form.company_type} onChange={(v) => setField("company_type", v)} options={COMPANY_TYPE_OPTIONS} testId="field-type" />
            <Field label="Email" value={form.email} onChange={(v) => setField("email", v)} testId="field-email" />
            <DateField label="Incorporation Date" value={form.incorporation_date} onChange={(v) => setField("incorporation_date", v)} testId="field-incorp" />
            <DateField label="FY End Date" value={form.fy_end_date} onChange={(v) => setField("fy_end_date", v)} testId="field-fy-end" />
            <Field label="Authorized Capital" value={form.authorized_capital} onChange={(v) => setField("authorized_capital", v)} testId="field-auth-cap" />
            <Field label="Paid-up Capital" value={form.paid_up_capital} onChange={(v) => setField("paid_up_capital", v)} testId="field-paid-cap" />
            <div className="md:col-span-2">
              <Field label="Registered Address" value={form.registered_address} onChange={(v) => setField("registered_address", v)} testId="field-address" textarea />
            </div>
          </div>
        </section>

        {/* Directors */}
        <ListSection
          title="Directors"
          items={form.directors}
          empty={EMPTY_DIRECTOR}
          addLabel="Add Director"
          onAdd={() => addArrayItem("directors", EMPTY_DIRECTOR)}
          onRemove={(i) => removeArrayItem("directors", i)}
          render={(d, i) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" value={d.name} onChange={(v) => updateArrayItem("directors", i, "name", v)} testId={`director-name-${i}`} />
              <Field label="DIN" value={d.din} onChange={(v) => updateArrayItem("directors", i, "din", v)} testId={`director-din-${i}`} />
              <Field label="Designation" value={d.designation} onChange={(v) => updateArrayItem("directors", i, "designation", v)} testId={`director-desig-${i}`} />
              <DateField label="Appointment Date" value={d.appointment_date} onChange={(v) => updateArrayItem("directors", i, "appointment_date", v)} testId={`director-appt-${i}`} />
            </div>
          )}
          testIdPrefix="director"
          minOne
        />

        {/* Members */}
        <MembersSection
          members={form.members || []}
          directors={(form.directors || []).filter((d) => d.name?.trim())}
          onAddBlank={() => addArrayItem("members", EMPTY_MEMBER)}
          onAddFromDirector={(d) => {
            // Skip if this director is already a member (match by DIN or name)
            const exists = (form.members || []).some(
              (m) => (d.din && m.din === d.din) || (m.name?.trim().toLowerCase() === d.name?.trim().toLowerCase()),
            );
            if (exists) {
              toast.info(`${d.name} is already added as a member`);
              return;
            }
            setForm((f) => ({
              ...f,
              members: [
                ...(f.members || []),
                {
                  ...EMPTY_MEMBER,
                  name: d.name,
                  din: d.din || "",
                  email: d.email || "",
                  source: "director",
                  _rid: _rowId(),
                },
              ],
            }));
          }}
          onRemove={(i) => removeArrayItem("members", i)}
          onUpdate={(i, k, v) => updateArrayItem("members", i, k, v)}
        />

        {/* Professionals */}
        <ListSection
          icon={Briefcase}
          title="Professionals"
          subtitle="Auditors, Company Secretary, Tax Advisor, etc. Reused across resolutions and filings."
          items={form.professionals}
          empty={EMPTY_PROFESSIONAL}
          addLabel="Add Professional"
          onAdd={() => addArrayItem("professionals", EMPTY_PROFESSIONAL)}
          onRemove={(i) => removeArrayItem("professionals", i)}
          render={(p, i) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name *" value={p.name} onChange={(v) => updateArrayItem("professionals", i, "name", v)} testId={`prof-name-${i}`} />
              <SelectField label="Role" value={p.role} onChange={(v) => updateArrayItem("professionals", i, "role", v)} options={ROLE_OPTIONS} testId={`prof-role-${i}`} />
              <Field label="Firm Name" value={p.firm_name} onChange={(v) => updateArrayItem("professionals", i, "firm_name", v)} testId={`prof-firm-${i}`} />
              <Field label="Membership No." value={p.membership_no} onChange={(v) => updateArrayItem("professionals", i, "membership_no", v)} testId={`prof-memno-${i}`} placeholder="ICAI / ICSI / ICMAI no." />
              <Field label="Firm Registration No. (FRN)" value={p.firm_registration_no} onChange={(v) => updateArrayItem("professionals", i, "firm_registration_no", v)} testId={`prof-frn-${i}`} />
              <Field label="Email" value={p.email} onChange={(v) => updateArrayItem("professionals", i, "email", v)} testId={`prof-email-${i}`} />
              <Field label="Phone" value={p.phone} onChange={(v) => updateArrayItem("professionals", i, "phone", v)} testId={`prof-phone-${i}`} />
              <DateField label="Appointment Date" value={p.appointment_date} onChange={(v) => updateArrayItem("professionals", i, "appointment_date", v)} testId={`prof-appt-${i}`} />
              <DateField label="Term End Date" value={p.term_end_date} onChange={(v) => updateArrayItem("professionals", i, "term_end_date", v)} testId={`prof-term-${i}`} />
              <div className="md:col-span-2">
                <Field label="Notes" value={p.notes} onChange={(v) => updateArrayItem("professionals", i, "notes", v)} testId={`prof-notes-${i}`} textarea />
              </div>
            </div>
          )}
          testIdPrefix="prof"
          emptyMsg="No professionals on record."
        />

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={() => navigate(-1)} className="swiss-btn-ghost" data-testid="cancel-edit">Cancel</button>
          <button onClick={save} disabled={saving} className="swiss-btn-primary" data-testid="save-edit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ListSection({ icon: Icon, title, subtitle, items = [], onAdd, onRemove, addLabel, render, testIdPrefix, minOne, emptyMsg }) {
  return (
    <section className="border border-zinc-200 bg-white" data-testid={`${testIdPrefix}-section`}>
      <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            {Icon && <Icon size={13} className="text-[#002FA7]" />}
            {title}
          </div>
          {subtitle && <p className="text-[11px] text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <button onClick={onAdd} className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid={`${testIdPrefix}-add`}>
          <Plus size={14} /> {addLabel}
        </button>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-sm text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
          {emptyMsg || "Nothing here yet."}
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((it, i) => (
            <div
              key={it._rid || `${testIdPrefix}-${i}`}
              className="p-6"
              data-testid={`${testIdPrefix}-row-${i}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="label-eyebrow">{title.replace(/s$/, "")} {i + 1}</span>
                {(items.length > 1 || !minOne) && (
                  <button onClick={() => onRemove(i)} className="text-zinc-400 hover:text-red-600" data-testid={`${testIdPrefix}-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              {render(it, i)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Field({ label, value, onChange, testId, placeholder = "", textarea = false }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <Cmp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        rows={textarea ? 3 : undefined}
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 focus:border-[#002FA7] bg-white"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 bg-white"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function MembersSection({ members, directors, onAddBlank, onAddFromDirector, onRemove, onUpdate }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const memberDins = new Set(
    (members || []).map((m) => (m.din || "").trim()).filter(Boolean),
  );
  const memberNames = new Set(
    (members || []).map((m) => (m.name || "").trim().toLowerCase()).filter(Boolean),
  );
  const availableDirectors = (directors || []).filter((d) => {
    const din = (d.din || "").trim();
    const nm = (d.name || "").trim().toLowerCase();
    if (din && memberDins.has(din)) return false;
    if (nm && memberNames.has(nm)) return false;
    return true;
  });

  return (
    <section className="border border-zinc-200 bg-white" data-testid="member-section">
      <div className="border-b border-zinc-200 px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Users size={13} className="text-[#002FA7]" />
            Members / Shareholders
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            Required for AGM/EGM notices and Shareholder Resolutions. Pick directors who also hold
            shares, or add fresh members.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="relative">
            <button
              onClick={() => setPickerOpen((o) => !o)}
              disabled={availableDirectors.length === 0}
              className="text-sm text-[#002FA7] hover:underline flex items-center gap-1 disabled:text-zinc-400 disabled:no-underline disabled:cursor-not-allowed"
              data-testid="member-pick-director-btn"
            >
              <Users size={14} /> Pick from Directors <ChevronDown size={12} />
            </button>
            {pickerOpen && availableDirectors.length > 0 && (
              <div
                className="absolute right-0 top-full mt-1 z-20 w-72 border border-zinc-200 bg-white shadow-lg max-h-64 overflow-y-auto"
                data-testid="member-director-picker"
              >
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
                  Add as Member
                </div>
                {availableDirectors.map((d, di) => (
                  <button
                    key={`${d.din || d.name}-${di}`}
                    onClick={() => {
                      onAddFromDirector(d);
                      setPickerOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 border-b border-zinc-50 last:border-0 flex flex-col"
                    data-testid={`member-director-option-${di}`}
                  >
                    <span className="text-zinc-900">{d.name}</span>
                    <span className="text-[11px] text-zinc-500">
                      {d.designation || "Director"}
                      {d.din ? ` · DIN ${d.din}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onAddBlank}
            className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
            data-testid="member-add"
          >
            <Plus size={14} /> Add Member
          </button>
        </div>
      </div>
      {(!members || members.length === 0) ? (
        <div className="p-6 text-center text-sm text-zinc-500" data-testid="member-empty">
          No members added.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {members.map((m, i) => (
            <div key={m._rid || `member-${i}`} className="p-6" data-testid={`member-row-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="label-eyebrow flex items-center gap-2">
                  Member {i + 1}
                  {m.source === "director" && (
                    <span className="text-[9px] tracking-wider text-[#002FA7] border border-[#002FA7]/30 px-1.5 py-0.5">
                      FROM DIRECTOR
                    </span>
                  )}
                </span>
                <button
                  onClick={() => onRemove(i)}
                  className="text-zinc-400 hover:text-red-600"
                  data-testid={`member-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name" value={m.name} onChange={(v) => onUpdate(i, "name", v)} testId={`member-name-${i}`} />
                <Field label="Folio No." value={m.folio_no || ""} onChange={(v) => onUpdate(i, "folio_no", v)} testId={`member-folio-${i}`} />
                <Field label="Email" value={m.email} onChange={(v) => onUpdate(i, "email", v)} testId={`member-email-${i}`} />
                <SelectField label="Member Status" value={m.status || "Individual"} onChange={(v) => onUpdate(i, "status", v)} options={MEMBER_STATUS_OPTIONS} testId={`member-status-${i}`} />
                <SelectField label="Share Class" value={m.member_type} onChange={(v) => onUpdate(i, "member_type", v)} options={MEMBER_TYPE_OPTIONS} testId={`member-type-${i}`} />
                <Field label="Face Value per Share (INR)" value={m.face_value ?? 0} onChange={(v) => onUpdate(i, "face_value", v)} testId={`member-face-value-${i}`} />
                <Field label="Shares Held" value={m.shares_held} onChange={(v) => onUpdate(i, "shares_held", v)} testId={`member-shares-${i}`} />
                <Field label="Shareholding %" value={m.shareholding_percent} onChange={(v) => onUpdate(i, "shareholding_percent", v)} testId={`member-percent-${i}`} />
                <Field label="Father's / Mother's / Spouse's Name" value={m.father_name || ""} onChange={(v) => onUpdate(i, "father_name", v)} testId={`member-father-${i}`} />
                <Field label="Occupation" value={m.occupation || ""} onChange={(v) => onUpdate(i, "occupation", v)} testId={`member-occupation-${i}`} />
                <Field label="PAN" value={m.pan_no || ""} onChange={(v) => onUpdate(i, "pan_no", v.toUpperCase())} testId={`member-pan-${i}`} />
                <Field label="Unique Identification No. (Aadhaar / Passport / CIN)" value={m.unique_id_no || ""} onChange={(v) => onUpdate(i, "unique_id_no", v)} testId={`member-uin-${i}`} />
                <Field label="Nationality" value={m.nationality || "Indian"} onChange={(v) => onUpdate(i, "nationality", v)} testId={`member-nationality-${i}`} />
                <Field label="DIN (if director-member)" value={m.din || ""} onChange={(v) => onUpdate(i, "din", v)} testId={`member-din-${i}`} />
                <div className="md:col-span-2">
                  <Field label="Address" value={m.address} onChange={(v) => onUpdate(i, "address", v)} testId={`member-address-${i}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
